import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { dashboardApi } from '../api'
import { DashboardStats, Activity, STAGE_LABELS } from '../types'

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentActivity, setRecentActivity] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    try {
      const [statsData, activityData] = await Promise.all([
        dashboardApi.stats(),
        dashboardApi.recentActivity(),
      ])
      setStats(statsData)
      setRecentActivity(activityData)
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Loading...</div>
  }

  if (!stats) {
    return <div className="text-center py-8">Failed to load dashboard</div>
  }

  const statCards = [
    { label: 'Total Leads', value: stats.total_leads, color: 'bg-blue-500', link: '/leads' },
    { label: "Today's Follow-ups", value: stats.today_followups, color: 'bg-orange-500', link: '/followups' },
    { label: 'Interested', value: stats.interested, color: 'bg-yellow-500', link: '/pipeline' },
    { label: 'Won', value: stats.won, color: 'bg-green-500', link: '/pipeline' },
    { label: 'Lost', value: stats.lost, color: 'bg-red-500', link: '/pipeline' },
  ]

  return (
    <div className="pb-20 lg:pb-0">
      <h1 className="text-xl lg:text-2xl font-bold mb-4 lg:mb-6">Dashboard</h1>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 mb-6 lg:mb-8">
        {statCards.map((stat) => (
          <Link
            key={stat.label}
            to={stat.link}
            className="card hover:shadow-md transition-shadow"
          >
            <div className={`w-10 h-10 lg:w-12 lg:h-12 ${stat.color} rounded-lg flex items-center justify-center mb-2 lg:mb-3`}>
              <span className="text-lg lg:text-2xl text-white font-bold">{stat.value}</span>
            </div>
            <h3 className="text-xs lg:text-sm font-medium text-gray-600">{stat.label}</h3>
            <p className="text-xl lg:text-2xl font-bold mt-1">{stat.value}</p>
          </Link>
        ))}
      </div>

      {/* Pipeline Overview */}
      <div className="card mb-6 lg:mb-8">
        <h2 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4">Pipeline Overview</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:gap-4">
          {Object.entries(stats.pipeline).map(([stage, count]) => (
            <div key={stage} className="text-center p-2 lg:p-4 bg-gray-50 rounded-lg">
              <div className="text-lg lg:text-2xl font-bold">{count as number}</div>
              <div className="text-xs lg:text-sm text-gray-600 mt-1">
                {STAGE_LABELS[stage as keyof typeof STAGE_LABELS]}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="flex justify-between items-center mb-3 lg:mb-4">
          <h2 className="text-base lg:text-lg font-semibold">Recent Activity</h2>
          <Link to="/leads" className="text-primary-600 hover:text-primary-700 text-xs lg:text-sm font-medium">
            View all leads
          </Link>
        </div>
        
        {recentActivity.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No recent activity</p>
        ) : (
          <div className="space-y-2 lg:space-y-3">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start gap-2 lg:gap-3 p-2 lg:p-3 bg-gray-50 rounded-lg">
                <div className="w-7 h-7 lg:w-8 lg:h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 text-xs lg:text-sm">
                    {activity.type === 'call' ? '📞' : 
                     activity.type === 'whatsapp' ? '💬' : 
                     activity.type === 'email' ? '📧' : 
                     activity.type === 'meeting' ? '🤝' : 
                     activity.type === 'demo' ? '🎯' : '📝'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs lg:text-sm font-medium truncate">{activity.description}</p>
                  <p className="text-xs text-gray-500 mt-0.5 lg:mt-1">
                    {new Date(activity.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
